import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Spinner, Alert, Table, Accordion } from 'react-bootstrap';
import {
    FiArrowLeft,
    FiUser,
    FiMail,
    FiPhone,
    FiMapPin,
    FiCalendar,
    FiGlobe,
    FiHome,
    FiAlertTriangle,
    FiExternalLink,
    FiBriefcase,
    FiCreditCard,
    FiShield,
    FiHash,
    FiInfo,
    FiEye,
    FiEyeOff,
} from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClientByIdRequest } from '../../store/actions/clientActions';
import { getClientLcbft } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import './AdminClientDetail.css';

const Label = ({ children, icon: Icon }) => (
    <div className="detail-label" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
        {Icon ? <Icon className="me-1" style={{ verticalAlign: '-0.1em' }} aria-hidden="true" /> : null}
        {children}
    </div>
);

const Value = ({ children }) => (
    <div className="detail-value" style={{ fontWeight: 600, color: 'var(--text-primary)', minHeight: '24px' }}>
        {children}
    </div>
);

const Section = ({ title, children, className = '' }) => (
    <Card className={`mb-3 ${className}`.trim()}>
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const isEmptyValue = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed === '' || trimmed.toLowerCase() === 'unknown' || trimmed.toLowerCase() === 'n/a';
    }
    return false;
};

const formatDisplayDate = (value) => {
    if (isEmptyValue(value)) return null;
    const raw = String(value).trim();
    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
        const [, y, m, d] = iso;
        return `${d}/${m}/${y}`;
    }
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    }
    return raw;
};

const fullName = (person) => {
    if (!person) return null;
    const name = [person.first_name, person.last_name]
        .filter((part) => !isEmptyValue(part))
        .map((part) => String(part).trim())
        .join(' ');
    return name || null;
};

const statusBadgeVariant = (status) => {
    const normalized = String(status || '').trim().toLowerCase();
    if (normalized === 'active' || normalized === 'actif') return 'success';
    if (normalized === 'inactive' || normalized === 'inactif') return 'secondary';
    if (normalized === 'suspended' || normalized === 'suspendu') return 'warning';
    if (normalized === 'blocked' || normalized === 'bloqué' || normalized === 'bloque') return 'danger';
    return 'primary';
};

const MISSING_LABEL = 'Non renseigné';

const MissingValue = () => (
    <span className="detail-value-missing">{MISSING_LABEL}</span>
);

const displayText = (value) => {
    if (isEmptyValue(value)) return <MissingValue />;
    return String(value).trim();
};

const displayBoolean = (value) => {
    if (value === null || value === undefined) return <MissingValue />;
    return (
        <Badge bg={value ? 'success' : 'secondary'}>
            {value ? 'Oui' : 'Non'}
        </Badge>
    );
};

const resolveClientAddress = (client) => {
    const candidates = [client?.client_address, client?.address, client?.adress];
    const found = candidates.find((value) => !isEmptyValue(value));
    return found ? String(found).trim() : null;
};

const countMissingFields = (client, fields) => (
    fields.filter(({ getValue }) => isEmptyValue(getValue(client))).length
);

const maskIban = (iban) => {
    if (!iban || iban === '—') return iban;
    const cleanedIban = String(iban).replace(/\s/g, '');
    if (cleanedIban.length < 7) return iban;
    const prefix = cleanedIban.substring(0, 4);
    const suffix = cleanedIban.substring(cleanedIban.length - 2);
    const middleLength = cleanedIban.length - 6;
    return `${prefix}${'*'.repeat(middleLength)}${suffix}`;
};

const getFullIban = (client) => {
    if (isEmptyValue(client?.iban)) return null;
    return String(client.iban).trim();
};

const getMaskedIban = (client) => {
    if (!isEmptyValue(client?.iban_masked)) return String(client.iban_masked).trim();
    const fullIban = getFullIban(client);
    if (fullIban) return maskIban(fullIban);
    return null;
};

const hasIbanData = (client) => Boolean(getFullIban(client) || getMaskedIban(client));

/** Motif de différence : Non renseigné si pas de titulaire ; sinon valeur API. */
const displayReasonOfDifference = (client) => {
    if (!client || isEmptyValue(client.account_holder_name)) {
        return <MissingValue />;
    }
    return displayText(client.reason_of_difference);
};

const displayOtherReasonComment = (client) => {
    if (!client || isEmptyValue(client.account_holder_name)) {
        return <MissingValue />;
    }
    return displayText(client.other_reason_comment);
};

/** Champ affiché même si la valeur est absente (état « Non renseigné »). */
const Field = ({ md = 6, icon, label, children }) => (
    <Col md={md} className="mb-3">
        <Label icon={icon}>{label}</Label>
        <Value>{children}</Value>
    </Col>
);

const AdminClientDetail = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { clientId } = useParams();
    const { selectedClient } = useSelector((state) => state.clients);
    const [lcbftData, setLcbftData] = useState(null);
    const [lcbftLoading, setLcbftLoading] = useState(false);
    const [lcbftError, setLcbftError] = useState(null);
    const [showIban, setShowIban] = useState(false);

    useEffect(() => {
        if (clientId) {
            dispatch(fetchClientByIdRequest(clientId));
            setShowIban(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    const clientInfo = selectedClient?.data?.client || selectedClient?.data || null;
    const partnerInfo = selectedClient?.data?.partner || null;
    const hasPartner = partnerInfo && typeof partnerInfo === 'object' && Object.keys(partnerInfo).length > 0;
    const lcbftChecked = clientInfo?.lcbft_checked === true;
    const lcbftCheckedRib = clientInfo?.lcbft_checked_rib === true;
    const shouldShowLcbftSection = lcbftChecked || lcbftCheckedRib;

    const lcbftVerificationLabel = () => {
        if (lcbftChecked && lcbftCheckedRib) return 'Vérifications client et RIB';
        if (lcbftCheckedRib) return 'Vérification RIB';
        if (lcbftChecked) return 'Vérification client';
        return null;
    };

    useEffect(() => {
        if (!clientId || !shouldShowLcbftSection) {
            setLcbftData(null);
            setLcbftError(null);
            setLcbftLoading(false);
            return;
        }

        const fetchLcbftData = async () => {
            try {
                setLcbftLoading(true);
                setLcbftError(null);
                const data = await getClientLcbft(clientId);
                setLcbftData(data);
            } catch (error) {
                const errorMessage = handleApiError(error, 'Erreur lors du chargement des données LCBFT');
                setLcbftError(errorMessage);
                setLcbftData(null);
            } finally {
                setLcbftLoading(false);
            }
        };
        fetchLcbftData();
    }, [clientId, shouldShowLcbftSection, lcbftChecked, lcbftCheckedRib]);

    if (selectedClient.loading) {
        return (
            <div className="dashboard-page admin-client-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du client</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/admin/client')}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement du client...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (selectedClient.error) {
        return (
            <div className="dashboard-page admin-client-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du client</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/admin/client')}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <Alert variant="danger">{selectedClient.error}</Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!clientInfo) {
        return (
            <div className="dashboard-page admin-client-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du client</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/admin/client')}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-muted">Aucune donnée de client disponible.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const clientName = fullName(clientInfo);
    const clientBirthDate = formatDisplayDate(clientInfo.birth_date);
    const clientJoinedDate = formatDisplayDate(clientInfo.date_joined);
    const clientSubmissionDate = formatDisplayDate(clientInfo.submission_date);
    const clientAddress = resolveClientAddress(clientInfo);
    const partnerCompanyName = hasPartner && !isEmptyValue(partnerInfo.company_name)
        ? partnerInfo.company_name
        : null;

    const profileCompletenessChecks = [
        { key: 'email', getValue: (c) => c.email },
        { key: 'phone', getValue: (c) => c.phone_number },
        { key: 'birth_date', getValue: (c) => c.birth_date },
        { key: 'address', getValue: (c) => resolveClientAddress(c) },
        { key: 'city', getValue: (c) => c.city },
        { key: 'country', getValue: (c) => c.country },
        { key: 'iban', getValue: (c) => c.iban_masked || c.iban },
    ];
    const missingProfileCount = countMissingFields(clientInfo, profileCompletenessChecks);

    const sectionTitle = (
        <span className="d-inline-flex align-items-center flex-wrap gap-2">
            <FiUser className="me-1" aria-hidden="true" />
            {clientName || 'Client'}
            <Badge bg="info">Client</Badge>
            {!isEmptyValue(clientInfo.status) && (
                <Badge bg={statusBadgeVariant(clientInfo.status)}>{clientInfo.status}</Badge>
            )}
        </span>
    );

    return (
        <div className="dashboard-page admin-client-detail-page">
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2 className="page-title">
                                <FiUser className="me-2" aria-hidden="true" />
                                Détail du client
                            </h2>
                            <p className="text-muted mb-0">Informations principales du client.</p>
                        </div>
                        <Button variant="outline-secondary" onClick={() => navigate('/admin/client')}>
                            <FiArrowLeft className="me-2" aria-hidden="true" /> Retour
                        </Button>
                    </div>

                    {missingProfileCount > 0 && (
                        <Alert variant="warning" className="mb-3 py-2">
                            <FiInfo className="me-2" aria-hidden="true" />
                            <strong>Informations incomplètes :</strong>{' '}
                            {missingProfileCount} champ(s) clé(s) non renseigné(s). Les valeurs manquantes sont
                            {' '}indiquées ci-dessous.
                        </Alert>
                    )}

                    <Section title={sectionTitle}>
                        <Row>
                            <Field icon={FiUser} label="Prénom">
                                {displayText(clientInfo.first_name)}
                            </Field>
                            <Field icon={FiUser} label="Nom">
                                {displayText(clientInfo.last_name)}
                            </Field>
                            <Field icon={FiHash} label="Code client">
                                {displayText(clientInfo.client_code)}
                            </Field>
                            <Field icon={FiUser} label="Civilité">
                                {displayText(clientInfo.title)}
                            </Field>
                            <Field icon={FiBriefcase} label="Rôle">
                                {!isEmptyValue(clientInfo.role) ? (
                                    <Badge bg="info">{clientInfo.role}</Badge>
                                ) : (
                                    <MissingValue />
                                )}
                            </Field>
                            <Field icon={FiUser} label="Statut">
                                {!isEmptyValue(clientInfo.status) ? (
                                    <Badge bg={statusBadgeVariant(clientInfo.status)}>{clientInfo.status}</Badge>
                                ) : (
                                    <MissingValue />
                                )}
                            </Field>
                            <Field icon={FiCalendar} label="Date d'inscription">
                                {clientJoinedDate || <MissingValue />}
                            </Field>
                            <Field icon={FiCalendar} label="Date de soumission">
                                {clientSubmissionDate || <MissingValue />}
                            </Field>
                            <Field icon={FiBriefcase} label="Nom de l'entreprise (partenaire)">
                                {partnerCompanyName || <MissingValue />}
                            </Field>
                        </Row>
                    </Section>

                    <Section title="Contact">
                        <Row>
                            <Field icon={FiMail} label="Email">
                                {displayText(clientInfo.email)}
                            </Field>
                            <Field icon={FiPhone} label="Téléphone">
                                {displayText(clientInfo.phone_number)}
                            </Field>
                            <Field icon={FiGlobe} label="Indicatif pays">
                                {displayText(clientInfo.country_code)}
                            </Field>
                        </Row>
                    </Section>

                    <Section title="Adresse">
                        <Row>
                            <Field md={12} icon={FiHome} label="Adresse">
                                {clientAddress ? (
                                    <span style={{ whiteSpace: 'pre-line' }}>{clientAddress}</span>
                                ) : (
                                    <MissingValue />
                                )}
                            </Field>
                            <Field icon={FiMapPin} label="Ville">
                                {displayText(clientInfo.city)}
                            </Field>
                            <Field icon={FiMapPin} label="Code postal">
                                {displayText(clientInfo.zip_code)}
                            </Field>
                            <Field icon={FiGlobe} label="Pays">
                                {displayText(clientInfo.country)}
                            </Field>
                        </Row>
                    </Section>

                    <Section title="Naissance">
                        <Row>
                            <Field icon={FiCalendar} label="Date de naissance">
                                {clientBirthDate || <MissingValue />}
                            </Field>
                            <Field icon={FiMapPin} label="Lieu de naissance">
                                {displayText(clientInfo.place_of_birth)}
                            </Field>
                            <Field icon={FiMapPin} label="Ville de naissance">
                                {displayText(clientInfo.city_of_birth)}
                            </Field>
                            <Field icon={FiGlobe} label="Pays de naissance">
                                {displayText(clientInfo.country_of_birth)}
                            </Field>
                        </Row>
                    </Section>

                    <Section title="Coordonnées bancaires">
                        <Row>
                            <Field md={12} icon={FiUser} label="Titulaire du compte">
                                {displayText(clientInfo.account_holder_name)}
                            </Field>
                            <Field icon={FiCreditCard} label="IBAN">
                                {hasIbanData(clientInfo) ? (
                                    <span className="d-inline-flex align-items-center gap-2 flex-wrap">
                                        <code>
                                            {showIban && getFullIban(clientInfo)
                                                ? getFullIban(clientInfo)
                                                : getMaskedIban(clientInfo)}
                                        </code>
                                        {getFullIban(clientInfo) && (
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="iban-toggle-btn p-0"
                                                onClick={() => setShowIban((prev) => !prev)}
                                                title={showIban ? 'Masquer l\'IBAN' : 'Afficher l\'IBAN'}
                                                aria-label={showIban ? 'Masquer l\'IBAN' : 'Afficher l\'IBAN'}
                                            >
                                                {showIban ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                            </Button>
                                        )}
                                    </span>
                                ) : (
                                    <MissingValue />
                                )}
                            </Field>
                            <Field icon={FiCreditCard} label="BIC">
                                {displayText(clientInfo.bic)}
                            </Field>
                            <Field icon={FiCreditCard} label="RIB reçu">
                                {displayBoolean(clientInfo.is_iban_received)}
                            </Field>
                        </Row>
                    </Section>

                    <Section title="Conformité et consentements">
                        <Row>
                            <Field icon={FiShield} label="Vérification LCBFT client">
                                {displayBoolean(clientInfo.lcbft_checked)}
                            </Field>
                            <Field icon={FiShield} label="Vérification LCBFT RIB">
                                {displayBoolean(clientInfo.lcbft_checked_rib)}
                            </Field>
                            <Field icon={FiShield} label="Attestation pertes">
                                {displayBoolean(clientInfo.attestation_losses)}
                            </Field>
                            <Field icon={FiShield} label="Consentement traitement des données">
                                {displayBoolean(clientInfo.consent_data_processing)}
                            </Field>
                            <Field md={12} icon={FiInfo} label="Motif de différence Nom du titulaire du compte bancaire">
                                {displayReasonOfDifference(clientInfo)}
                            </Field>
                            <Field md={12} icon={FiInfo} label="Commentaire (autre motif)">
                                {displayOtherReasonComment(clientInfo)}
                            </Field>
                        </Row>
                    </Section>

                    {shouldShowLcbftSection && (
                        <Section title="Informations LCBFT">
                            <Alert variant="info" className="mb-3 py-2">
                                <strong>Contrôle actif :</strong> {lcbftVerificationLabel()}
                            </Alert>
                            {lcbftLoading ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-3 text-muted">Chargement des données LCBFT...</p>
                                </div>
                            ) : lcbftError ? (
                                <Alert variant="danger">
                                    <FiAlertTriangle className="me-2" aria-hidden="true" />
                                    {lcbftError}
                                </Alert>
                            ) : lcbftData?.lcbft_profile ? (
                                <>
                                    <Row>
                                        <Col md={6} className="mb-3">
                                            <Label>Sanctions en cours</Label>
                                            <Value>
                                                <Badge bg={lcbftData.lcbft_profile.sanctions_en_cours ? 'danger' : 'success'}>
                                                    {lcbftData.lcbft_profile.sanctions_en_cours ? 'Oui' : 'Non'}
                                                </Badge>
                                            </Value>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <Label>Personne politiquement exposée</Label>
                                            <Value>
                                                <Badge bg={lcbftData.lcbft_profile.personne_politiquement_exposee ? 'warning' : 'success'}>
                                                    {lcbftData.lcbft_profile.personne_politiquement_exposee ? 'Oui' : 'Non'}
                                                </Badge>
                                            </Value>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <Label icon={FiCalendar}>Date de création</Label>
                                            <Value>
                                                {lcbftData.lcbft_profile.created_at
                                                    ? new Date(lcbftData.lcbft_profile.created_at).toLocaleString('fr-FR')
                                                    : '—'}
                                            </Value>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <Label icon={FiCalendar}>Dernière mise à jour</Label>
                                            <Value>
                                                {lcbftData.lcbft_profile.updated_at
                                                    ? new Date(lcbftData.lcbft_profile.updated_at).toLocaleString('fr-FR')
                                                    : '—'}
                                            </Value>
                                        </Col>
                                    </Row>

                                    {lcbftData.lcbft_profile.personne_politiquement_exposee ? (
                                        <div className="mt-2 mb-4">
                                            <h6 className="mb-3">Personne politiquement exposée</h6>
                                            <Row>
                                                <Col md={4} className="mb-3">
                                                    <Label>Type</Label>
                                                    <Value>
                                                        {!isEmptyValue(lcbftData.lcbft_profile.personne_politiquement_exposee.type) ? (
                                                            <Badge bg="info">
                                                                {lcbftData.lcbft_profile.personne_politiquement_exposee.type}
                                                            </Badge>
                                                        ) : '—'}
                                                    </Value>
                                                </Col>
                                                <Col md={4} className="mb-3">
                                                    <Label>En cours</Label>
                                                    <Value>
                                                        <Badge bg={lcbftData.lcbft_profile.personne_politiquement_exposee.en_cours ? 'warning' : 'secondary'}>
                                                            {lcbftData.lcbft_profile.personne_politiquement_exposee.en_cours ? 'Oui' : 'Non'}
                                                        </Badge>
                                                    </Value>
                                                </Col>
                                            </Row>

                                            {lcbftData.lcbft_profile.personne_politiquement_exposee.fonctions?.length > 0 && (
                                                <Accordion className="mt-3">
                                                    <Accordion.Item eventKey="0">
                                                        <Accordion.Header>
                                                            <strong>
                                                                Fonctions ({lcbftData.lcbft_profile.personne_politiquement_exposee.fonctions.length})
                                                            </strong>
                                                        </Accordion.Header>
                                                        <Accordion.Body>
                                                            <div className="table-responsive">
                                                                <Table striped bordered hover size="sm">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Fonction</th>
                                                                            <th>Pays</th>
                                                                            <th>Date début</th>
                                                                            <th>Date fin</th>
                                                                            <th>En cours</th>
                                                                            <th>Sources</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {lcbftData.lcbft_profile.personne_politiquement_exposee.fonctions.map((fonction, idx) => (
                                                                            <tr key={idx}>
                                                                                <td>{fonction.fonction || '—'}</td>
                                                                                <td>{fonction.pays || '—'}</td>
                                                                                <td>{fonction.date_debut || '—'}</td>
                                                                                <td>{fonction.date_fin || '—'}</td>
                                                                                <td>
                                                                                    <Badge bg={fonction.en_cours ? 'success' : 'secondary'}>
                                                                                        {fonction.en_cours ? 'Oui' : 'Non'}
                                                                                    </Badge>
                                                                                </td>
                                                                                <td>
                                                                                    {fonction.sources?.length > 0 ? (
                                                                                        <div>
                                                                                            {fonction.sources.map((source, sIdx) => (
                                                                                                <div key={sIdx} className="mb-1">
                                                                                                    <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                                                                                        {source.nom} <FiExternalLink size={12} aria-hidden="true" />
                                                                                                    </a>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    ) : '—'}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                            </div>
                                                        </Accordion.Body>
                                                    </Accordion.Item>
                                                </Accordion>
                                            )}

                                            {lcbftData.lcbft_profile.personne_politiquement_exposee.personnes_politiquement_exposees_associees?.length > 0 && (
                                                <Accordion className="mt-3">
                                                    <Accordion.Item eventKey="1">
                                                        <Accordion.Header>
                                                            <strong>
                                                                Personnes politiquement exposées associées ({lcbftData.lcbft_profile.personne_politiquement_exposee.personnes_politiquement_exposees_associees.length})
                                                            </strong>
                                                        </Accordion.Header>
                                                        <Accordion.Body>
                                                            <div className="table-responsive">
                                                                <Table striped bordered hover size="sm">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Nom</th>
                                                                            <th>Prénom</th>
                                                                            <th>Date de naissance</th>
                                                                            <th>Fonction</th>
                                                                            <th>Liens</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {lcbftData.lcbft_profile.personne_politiquement_exposee.personnes_politiquement_exposees_associees.map((personne, idx) => (
                                                                            <tr key={idx}>
                                                                                <td>{personne.nom || '—'}</td>
                                                                                <td>{personne.prenom || '—'}</td>
                                                                                <td>{personne.date_de_naissance || '—'}</td>
                                                                                <td>
                                                                                    {personne.fonction
                                                                                        ? (Array.isArray(personne.fonction) ? personne.fonction.join(', ') : personne.fonction)
                                                                                        : '—'}
                                                                                </td>
                                                                                <td>
                                                                                    {personne.liens?.length > 0 ? (
                                                                                        <div>
                                                                                            {personne.liens.map((lien, lIdx) => (
                                                                                                <div key={lIdx} className="mb-1">
                                                                                                    <Badge bg="secondary" className="me-1">
                                                                                                        {Array.isArray(lien.nom) ? lien.nom.join(', ') : lien.nom}
                                                                                                    </Badge>
                                                                                                    {lien.date_debut && (
                                                                                                        <small className="text-muted">
                                                                                                            ({lien.date_debut} - {lien.date_fin || 'en cours'})
                                                                                                        </small>
                                                                                                    )}
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    ) : '—'}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                            </div>
                                                        </Accordion.Body>
                                                    </Accordion.Item>
                                                </Accordion>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-muted mb-0 mt-2">
                                            Aucune personne politiquement exposée identifiée pour ce profil.
                                        </p>
                                    )}

                                    {lcbftData.lcbft_profile.sanctions?.length > 0 && (
                                        <Accordion className="mt-4">
                                            <Accordion.Item eventKey="2">
                                                <Accordion.Header>
                                                    <strong>Sanctions ({lcbftData.lcbft_profile.sanctions.length})</strong>
                                                </Accordion.Header>
                                                <Accordion.Body>
                                                    <div className="table-responsive">
                                                        <Table striped bordered hover size="sm">
                                                            <thead>
                                                                <tr>
                                                                    <th>Pays</th>
                                                                    <th>Autorité</th>
                                                                    <th>Date début</th>
                                                                    <th>Date fin</th>
                                                                    <th>En cours</th>
                                                                    <th>Description</th>
                                                                    <th>Sources</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {lcbftData.lcbft_profile.sanctions.map((sanction, idx) => (
                                                                    <tr key={idx}>
                                                                        <td>{sanction.pays || '—'}</td>
                                                                        <td>{sanction.autorite || '—'}</td>
                                                                        <td>{sanction.date_debut || '—'}</td>
                                                                        <td>{sanction.date_fin || '—'}</td>
                                                                        <td>
                                                                            <Badge bg={sanction.en_cours ? 'danger' : 'secondary'}>
                                                                                {sanction.en_cours ? 'Oui' : 'Non'}
                                                                            </Badge>
                                                                        </td>
                                                                        <td>{sanction.description || '—'}</td>
                                                                        <td>
                                                                            {sanction.sources?.length > 0 ? (
                                                                                <div>
                                                                                    {sanction.sources.map((source, sIdx) => (
                                                                                        <div key={sIdx} className="mb-1">
                                                                                            <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                                                                                {source.nom} <FiExternalLink size={12} aria-hidden="true" />
                                                                                            </a>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            ) : '—'}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>
                                    )}
                                </>
                            ) : (
                                <Alert variant="info">
                                    Aucune donnée LCBFT disponible pour ce client.
                                </Alert>
                            )}
                        </Section>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminClientDetail;
